<template>
  <div class="media-container">
    <img
      v-if="isImage"
      :data-src="fileUrl"
      :alt="altText"
      class="lazy-media"
      :class="{ 'loaded': isLoaded }"
      ref="mediaElement"
    />
    <video
      v-else-if="isVideo"
      :data-src="fileUrl"
      class="lazy-media"
      :class="{ 'loaded': isLoaded }"
      ref="mediaElement"
      controls
    >
      <source :src="fileUrl" :type="videoType" />
      Your browser does not support the video tag.
    </video>
    <div v-else class="error">Unsupported media type or Missing FireToKitchenOnHold</div>
  </div>
</template>

<script>
export default {
  name: 'MediaLazyLoad',
  props: {
    fileUrl: {
      type: String,
      required: true,
    },
    altText: {
      type: String,
      default: 'Media content',
    },
  },
  data() {
    return {
      isLoaded: false,
      observer: null,
    };
  },
  computed: {
    isImage() {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(this.fileUrl);
    },
    isVideo() {
      return /\.(mp4|webm|ogg)$/i.test(this.fileUrl);
    },
    videoType() {
      if (this.fileUrl.endsWith('.mp4')) return 'video/mp4';
      if (this.fileUrl.endsWith('.webm')) return 'video/webm';
      if (this.fileUrl.endsWith('.ogg')) return 'video/ogg';
      return '';
    },
  },
  mounted() {
    this.initObserver();
  },
  beforeDestroy() {
    this.disconnectObserver();
  },
  methods: {
    initObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.loadMedia();
                this.observer.unobserve(this.$refs.mediaElement);
              }
            });
          },
          { rootMargin: '50px' }
        );
        this.observer.observe(this.$refs.mediaElement);
      } else {
        // Fallback for browsers without IntersectionObserver
        this.loadMedia();
      }
    },
    loadMedia() {
      const media = this.$refs.mediaElement;
      if (media && media.dataset.src) {
        media.src = media.dataset.src;
        media.onload = () => {
          this.isLoaded = true;
        };
        if (this.isVideo) {
          media.load();
          this.isLoaded = true; // Videos may not trigger onload consistently
        }
      }
    },
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },
  },
};
</script>

<style scoped>
.media-container {
  display: flex;
  justify-content: right;
  align-items: right;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.lazy-media {
  max-width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.lazy-media.loaded {
  opacity: 1;
}

.error {
  color: red;
  font-size: 1rem;
}
</style>